import { create } from "zustand";
import { apiClient } from "../api/client";

export type ThemePref = "light" | "dark" | "system";
export type Effective = "light" | "dark";

interface ThemeState {
  pref: ThemePref;
  effective: Effective;
  setPref: (p: ThemePref) => void;
  setPrefSilent: (p: ThemePref) => void;
}

const STORAGE_KEY = "pmt-theme";

function readStoredPref(): ThemePref {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    // private mode / quota
  }
  return "system";
}

function resolveEffective(pref: ThemePref): Effective {
  if (pref === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return pref;
}

function applyHtmlClass(pref: ThemePref) {
  document.documentElement.classList.toggle(
    "dark",
    resolveEffective(pref) === "dark",
  );
}

const initialPref = readStoredPref();

export const useThemeStore = create<ThemeState>((set, get) => ({
  pref: initialPref,
  effective: resolveEffective(initialPref),
  // User-initiated theme change. Optimistic: applies locally first
  // (instant feedback), then fires PATCH /api/v1/me/preferences. On
  // HTTP error, reverts local state to keep store + server consistent
  // and logs a console warn (no toast UI in v1).
  setPref: (next) => {
    const previous = get().pref;
    set({ pref: next, effective: resolveEffective(next) });
    applyHtmlClass(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore storage failure
    }
    apiClient
      .PATCH("/api/v1/me/preferences", {
        params: { header: { "X-Requested-With": "xhr" } },
        body: { theme_preference: next },
      })
      .then(({ error }) => {
        if (error) {
          set({ pref: previous, effective: resolveEffective(previous) });
          applyHtmlClass(previous);
          try {
            window.localStorage.setItem(STORAGE_KEY, previous);
          } catch {
            // ignore
          }
          console.warn("theme PATCH failed; reverted to", previous, error);
        }
      })
      .catch((err) => {
        set({ pref: previous, effective: resolveEffective(previous) });
        applyHtmlClass(previous);
        try {
          window.localStorage.setItem(STORAGE_KEY, previous);
        } catch {
          // ignore
        }
        console.warn("theme PATCH failed; reverted to", previous, err);
      });
  },
  // Used by storage event handler (cross-tab sync) and useThemeSync
  // (server hydration). Skips localStorage write to avoid a cross-tab
  // write cascade, and skips PATCH because the value is already
  // authoritative.
  setPrefSilent: (next) => {
    if (get().pref === next) return;
    set({ pref: next, effective: resolveEffective(next) });
    applyHtmlClass(next);
  },
}));

// Module-level side effects: live-update on OS theme change (only
// meaningful when pref is "system" — for "light"/"dark" the effective
// resolution ignores the OS preference, so the listener is a no-op).
const mql = window.matchMedia("(prefers-color-scheme: dark)");
const handleMqlChange = () => {
  const { pref } = useThemeStore.getState();
  const nextEffective = resolveEffective(pref);
  useThemeStore.setState({ effective: nextEffective });
  applyHtmlClass(pref);
};
if (typeof mql.addEventListener === "function") {
  mql.addEventListener("change", handleMqlChange);
} else {
  // Safari < 14 fallback
  mql.addListener(handleMqlChange);
}

window.addEventListener("storage", (e) => {
  if (e.key !== STORAGE_KEY || !e.newValue) return;
  const next = e.newValue;
  if (next === "light" || next === "dark" || next === "system") {
    useThemeStore.getState().setPrefSilent(next);
  }
});
